import { createActionsHook, StoreActionApi } from 'react-sweet-state';

import {
  createDataHook,
  createGetAction,
  createGetOneHook,
  createLoadingHook,
  createRestActions,
  createRestStore,
  RestStoreState,
} from '@intelliarts/ia-react-template';

import { createMutationFailedCheck } from '../../../utils/restMutation';
import { EmploymentEventType, EmploymentPeriodWithEmployee } from '../models/EmploymentPeriodWithEmployee';

type EmploymentsStoreState = RestStoreState<EmploymentPeriodWithEmployee>;

const actionConfig = {
  resourcePath: '/employment-periods',
};

const genericActions = createRestActions<EmploymentPeriodWithEmployee>(actionConfig);

const employmentActions = {
  ...genericActions,
  loadByEventTypeAndRange: (eventType: EmploymentEventType, from: string, to: string) =>
    async ({ dispatch }: StoreActionApi<EmploymentsStoreState>) => {
      const getAction = createGetAction<EmploymentPeriodWithEmployee>({
        resourcePath: `${actionConfig.resourcePath}?eventType=${eventType}&from=${from}&to=${to}`,
      });
      await dispatch(getAction());
    },
  loadLatestByEmployeeId: (employeeId: string | number) =>
    async ({ dispatch }: StoreActionApi<EmploymentsStoreState>) => {
      const getAction = createGetAction<EmploymentPeriodWithEmployee>({
        resourcePath: `${actionConfig.resourcePath}/employees/${employeeId}/latest`,
      });
      await dispatch(getAction());
    },
  loadByEmployeeId: (employeeId: string | number) =>
    async ({ dispatch }: StoreActionApi<EmploymentsStoreState>) => {
      const getAction = createGetAction<EmploymentPeriodWithEmployee>({
        resourcePath: `${actionConfig.resourcePath}/employees/${employeeId}`,
      });
      await dispatch(getAction());
    },
  clearEmployments: () => ({ setState }: StoreActionApi<EmploymentsStoreState>) => {
      setState({ data: [], isLoading: false, changes: [] });
  },
};

export const EmploymentsStore = createRestStore<EmploymentPeriodWithEmployee>({
  actions: employmentActions,
  actionConfig,
  containerDisplayName: 'employments-page-container',
  onInitAction: null,
});

export const EmployeeEmploymentPeriodsStore = createRestStore<EmploymentPeriodWithEmployee>({
  actions: employmentActions,
  actionConfig,
  containerDisplayName: 'employee-employment-periods-container',
  onInitAction: null,
});

export const LatestEmploymentStore = createRestStore<EmploymentPeriodWithEmployee>({
  actions: employmentActions,
  actionConfig,
  containerDisplayName: 'latest-employment-container',
  onInitAction: null,
});

export const useEmploymentsData = createDataHook(EmploymentsStore);
export const useEmploymentsIsLoading = createLoadingHook(EmploymentsStore);
export const useEmploymentsActions = createActionsHook(EmploymentsStore);
export const useEmployeeEmploymentPeriodsData = createDataHook(EmployeeEmploymentPeriodsStore);
export const useEmployeeEmploymentPeriodsIsLoading = createLoadingHook(EmployeeEmploymentPeriodsStore);
export const useEmployeeEmploymentPeriodsActions = createActionsHook(EmployeeEmploymentPeriodsStore);
export const useLatestEmploymentData = createGetOneHook(LatestEmploymentStore);
export const useLatestEmploymentIsLoading = createLoadingHook(LatestEmploymentStore);
export const useLatestEmploymentActions = createActionsHook(LatestEmploymentStore);
// All employment stores mutate the same '/employment-periods' resource, so one check covers them.
export const didEmploymentsMutationFail = createMutationFailedCheck(actionConfig.resourcePath);

